import { Button, CheckBox, LoadingSpinner } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import Grid from '../../components/Grid/Grid';
import SearchBarExtended from '../../components/SearchBarExtended/SearchBarExtended';
import { useStore } from '../../hooks/use-store';
import { checkSinglePermission, useCheckSinglePermission } from '../../security/security-hook';
import {
  PERMISSION_ALLOW,
  RESOURCE_USER_MANAGEMENT_ADD_USER,
  RESOURCE_USER_MANAGEMENT_PRACTICE_USERS,
} from '../../types/users-types';
import { isDefined } from '../../utils/object-utils';
import { SubRowRenderer } from './renderers';
import './UsersManagement.scss';

const UsersManagement = observer(() => {
  const { usersManagementStore: store, globalStore } = useStore();
  const {
    colDefs,
    filteredUsers,
    loaders,
    hideGuests,
    searchText,
    sorting,
    expandedRowIds,
    practiceUsersMode,
    currentUserIndex,
  } = store;

  useEffect(() => {
    void store.init();
  }, [store]);

  const addUserBtnIsEnabled = checkSinglePermission(
    globalStore.root.apiStore.userGlobalPermissions,
    RESOURCE_USER_MANAGEMENT_ADD_USER,
    PERMISSION_ALLOW,
  );

  const practiceUsersCheckboxEnabled = useCheckSinglePermission(
    RESOURCE_USER_MANAGEMENT_PRACTICE_USERS,
    PERMISSION_ALLOW,
  );

  return (
    <div className='users-management-wrapper'>
      <div className='spot-typography__heading--level-2'>
        {practiceUsersMode ? 'Practice Users' : 'Users'}
      </div>
      <CheckBox
        label='Practice Users'
        id='users-mode'
        aria-label='Users Mode'
        size='small'
        checked={practiceUsersMode}
        disabled={loaders.page || currentUserIndex !== undefined || !practiceUsersCheckboxEnabled}
        onChange={() => void store.togglePracticeUsersMode()}
      />
      {loaders.page ? (
        <LoadingSpinner size='large' />
      ) : (
        <>
          <div className='page-top-actions user-management_filter'>
            <div>
              <Button
                className='add-more-btn'
                aria-label='Add More'
                variant='link'
                size='small'
                iconLeft='plus'
                disabled={
                  loaders.userAction ||
                  practiceUsersMode ||
                  currentUserIndex !== undefined ||
                  !addUserBtnIsEnabled
                }
                onClick={() => store.addUsersModal()}
              >
                Add Users
              </Button>
              {loaders.userAction && <LoadingSpinner />}
              <SearchBarExtended
                aria-label='Label Search'
                disabled={loaders.userAction || store.currentUserIndex !== undefined}
                maxLength={15}
                value={searchText}
                placeholder='Search User Email, First Name, Last Name'
                onSearchClick={v => runInAction(() => (store.searchText = v))}
              />
              <CheckBox
                id='hide-guests'
                label='Hide Guests'
                aria-label='Hide Guests'
                className='hide-guests skeleton item-editors'
                size='small'
                disabled={
                  loaders.userAction || store.currentUserIndex !== undefined || practiceUsersMode
                }
                checked={hideGuests}
                onChange={() => runInAction(() => (store.hideGuests = !store.hideGuests))}
              />
            </div>
          </div>
          <Grid
            id='users-management-grid'
            className='users-management-grid'
            columns={colDefs}
            data={filteredUsers}
            sorting={currentUserIndex === undefined ? sorting : undefined}
            subrowRenderer={SubRowRenderer}
            expandedRowIds={expandedRowIds}
            rowIdField='email'
          />
          <div className='page-bottom-actions'>
            <Button
              className='back item-editors'
              variant='link'
              size='small'
              onClick={() => store.back()}
            >
              Back
            </Button>
            <div className='main-buttons'>
              <Button
                variant='primary'
                size='small'
                disabled={loaders.userAction || isDefined(store.currentUserIndex)}
                onClick={() => store.done()}
              >
                Dashboard
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default UsersManagement;
